import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Plus, Edit2, Trash2, X, Save, Calendar, Tag, Upload, Play, GripVertical, Images, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSupabaseExperimental } from '../hooks/useSupabaseExperimental';
import { ResearchArticle, updateExperimental } from '../lib/supabase';
import { compressProductImage } from '../lib/imageCompression';

export default function ExperimentalPage() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'de' | 'cz' | 'sk';
  const location = useLocation();
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState<ResearchArticle | null>(null);
  const [draggedArticleIndex, setDraggedArticleIndex] = useState<number | null>(null);
  const [detailArticle, setDetailArticle] = useState<ResearchArticle | null>(null);

  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true' ||
                  localStorage.getItem('admin_logged_in') === 'true';

  const { articles, loading, addArticle, updateArticle, deleteArticle } = useSupabaseExperimental();

  const sortedArticles = [...articles].sort((a, b) => a.order_index - b.order_index);

  const openDetail = (article: ResearchArticle) => {
    setDetailArticle(article);
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const closeDetail = () => {
    setDetailArticle(null);
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const handleEditArticle = (article: ResearchArticle, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingArticle(article);
    setShowArticleModal(true);
  };

  const handleDeleteArticle = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Opravdu chcete smazat tuto technologii?')) {
      try {
        await deleteArticle(id);
      } catch (error) {
        alert('Chyba při mazání');
      }
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedArticleIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedArticleIndex === null || draggedArticleIndex === dropIndex) return;
    const reordered = [...sortedArticles];
    const dragged = reordered[draggedArticleIndex];
    reordered.splice(draggedArticleIndex, 1);
    reordered.splice(dropIndex, 0, dragged);
    try {
      await Promise.all(reordered.map((article, index) =>
        updateExperimental(article.id, { order_index: index })
      ));
      await updateArticle(reordered[0].id, { order_index: 0 });
    } catch (err) {
      console.error('Error updating order:', err);
    }
    setDraggedArticleIndex(null);
  };

  const getTitle = (article: ResearchArticle) => {
    if (currentLang === 'en' && article.title_en) return article.title_en;
    if (currentLang === 'de' && article.title_de) return article.title_de;
    if (currentLang === 'sk' && article.title_sk) return article.title_sk;
    return article.title_cz;
  };

  const getDescription = (article: ResearchArticle) => {
    if (currentLang === 'en' && article.description_en) return article.description_en;
    if (currentLang === 'de' && article.description_de) return article.description_de;
    if (currentLang === 'sk' && article.description_sk) return article.description_sk;
    return article.description_cz;
  };

  const getDisplayImage = (article: ResearchArticle) => {
    if (article.image_url) return article.image_url;
    if (article.images && article.images.length > 0) return article.images[0];
    return null;
  };

  const getAllImages = (article: ResearchArticle): string[] => {
    const imgs: string[] = [];
    if (article.image_url) imgs.push(article.image_url);
    if (article.images) imgs.push(...article.images);
    return imgs;
  };

  return (
    <div className="min-h-screen pt-20 pb-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Zap className="w-10 h-10 text-green-400" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent">
              Experimentální technologie
            </h1>
          </div>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Průlomové a experimentální technologické projekty
          </p>
          {isAdmin && (
            <div className="flex justify-center gap-4 mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => { setEditingArticle(null); setShowArticleModal(true); }}
                className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all"
              >
                <Plus className="w-5 h-5" />
                Přidat technologii
              </motion.button>
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
            <p className="text-slate-400 mt-4">Načítám...</p>
          </div>
        ) : sortedArticles.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
            <Zap className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">Zatím nejsou k dispozici žádné technologie.</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                draggable={isAdmin}
                onDragStart={isAdmin ? (e) => handleDragStart(e, index) : undefined}
                onDragOver={isAdmin ? handleDragOver : undefined}
                onDrop={isAdmin ? (e) => handleDrop(e, index) : undefined}
                onClick={() => !isAdmin && openDetail(article)}
                className={`group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-green-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/10 ${isAdmin ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${draggedArticleIndex === index ? 'opacity-50 scale-95' : ''}`}
              >
                {isAdmin && (
                  <div className="absolute top-2 left-2 z-10 bg-black/60 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical className="w-4 h-4 text-slate-400" />
                  </div>
                )}
                <div className="relative aspect-video bg-slate-900 overflow-hidden">
                  {getDisplayImage(article) ? (
                    <>
                      <img
                        src={getDisplayImage(article)!}
                        alt={getTitle(article)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      {getAllImages(article).length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Images className="w-3 h-3" />
                          {getAllImages(article).length}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Zap className="w-12 h-12 text-slate-700" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {article.video_url && (
                    <a
                      href={article.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    >
                      <div className="w-14 h-14 bg-green-500/90 rounded-full flex items-center justify-center shadow-lg shadow-green-500/50">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </a>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-green-400 transition-colors">
                    {getTitle(article)}
                  </h3>
                  <p className="text-slate-400 text-sm line-clamp-3 mb-4">{getDescription(article)}</p>
                  <div className="space-y-2 mb-4">
                    {article.date && (
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <Calendar className="w-4 h-4 text-green-400" />
                        <span>{article.date}</span>
                      </div>
                    )}
                    {article.category && (
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-green-400" />
                        <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full text-xs border border-green-500/20">
                          {article.category}
                        </span>
                      </div>
                    )}
                  </div>
                  {isAdmin ? (
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); openDetail(article); }}
                        className="flex-1 p-2 bg-green-700/60 hover:bg-green-600 rounded text-white transition-colors flex items-center justify-center gap-2 text-sm"
                      >
                        <Zap className="w-4 h-4" />
                        Detail
                      </button>
                      <button
                        onClick={(e) => handleEditArticle(article, e)}
                        className="flex-1 p-2 bg-blue-600/80 hover:bg-blue-500 rounded text-white transition-colors flex items-center justify-center gap-2 text-sm"
                      >
                        <Edit2 className="w-4 h-4" />
                        Upravit
                      </button>
                      <button
                        onClick={(e) => handleDeleteArticle(article.id, e)}
                        className="p-2 bg-red-600/80 hover:bg-red-500 rounded text-white transition-colors flex items-center justify-center"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-green-400 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Číst více
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {detailArticle && (
          <ArticleDetailModal
            article={detailArticle}
            getTitle={getTitle}
            getDescription={getDescription}
            getAllImages={getAllImages}
            onClose={closeDetail}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showArticleModal && (
          <ArticleEditModal
            article={editingArticle}
            onClose={() => setShowArticleModal(false)}
            onSave={async (articleData) => {
              try {
                if (editingArticle) {
                  await updateArticle(editingArticle.id, articleData);
                } else {
                  await addArticle(articleData as Omit<ResearchArticle, 'id' | 'created_at' | 'updated_at'>);
                }
                setShowArticleModal(false);
              } catch (error) {
                alert('Chyba při ukládání: ' + (error as Error).message);
              }
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ArticleDetailModal({
  article,
  getTitle,
  getDescription,
  getAllImages,
  onClose,
}: {
  article: ResearchArticle;
  getTitle: (a: ResearchArticle) => string;
  getDescription: (a: ResearchArticle) => string;
  getAllImages: (a: ResearchArticle) => string[];
  onClose: () => void;
}) {
  const images = getAllImages(article);
  const [currentImg, setCurrentImg] = useState(0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-slate-900 border border-green-500/30 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        {images.length > 0 && (
          <div className="relative aspect-video bg-slate-950 rounded-t-2xl overflow-hidden">
            <img src={images[currentImg]} alt={getTitle(article)} className="w-full h-full object-cover" />
            {images.length > 1 && (
              <>
                <button onClick={() => setCurrentImg(i => (i - 1 + images.length) % images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => setCurrentImg(i => (i + 1) % images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setCurrentImg(i)}
                      className={`w-2 h-2 rounded-full transition-all ${i === currentImg ? 'bg-green-400 w-4' : 'bg-white/40 hover:bg-white/70'}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4 pr-8">
            <h2 className="text-2xl font-bold text-white">{getTitle(article)}</h2>
          </div>
          <div className="flex flex-wrap gap-3 mb-5">
            {article.date && (
              <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                <Calendar className="w-4 h-4 text-green-400" />
                <span>{article.date}</span>
              </div>
            )}
            {article.category && (
              <span className="px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-xs border border-green-500/20 font-medium">
                {article.category}
              </span>
            )}
          </div>
          <p className="text-slate-300 leading-relaxed whitespace-pre-wrap mb-5">{getDescription(article)}</p>
          {article.video_url && (
            <a href={article.video_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white px-5 py-2.5 rounded-lg font-medium transition-colors">
              <Play className="w-4 h-4" />Přehrát video
            </a>
          )}
          {images.length > 1 && (
            <div className="mt-5">
              <p className="text-slate-400 text-sm mb-2">Galerie ({images.length} obrázků)</p>
              <div className="grid grid-cols-5 gap-2">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setCurrentImg(i)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${i === currentImg ? 'border-green-400' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function ArticleEditModal({
  article,
  onClose,
  onSave,
}: {
  article: ResearchArticle | null;
  onClose: () => void;
  onSave: (data: Partial<ResearchArticle>) => void;
}) {
  const [formData, setFormData] = useState({
    title_cz: article?.title_cz || '',
    title_en: article?.title_en || '',
    title_de: article?.title_de || '',
    title_sk: article?.title_sk || '',
    description_cz: article?.description_cz || '',
    description_en: article?.description_en || '',
    description_de: article?.description_de || '',
    description_sk: article?.description_sk || '',
    image_url: article?.image_url || '',
    images: article?.images || [] as string[],
    video_url: article?.video_url || '',
    date: article?.date || new Date().toISOString().split('T')[0],
    category: article?.category || '',
    order_index: article?.order_index ?? 0,
  });
  const [imageUploading, setImageUploading] = useState(false);
  const [draggedImgIdx, setDraggedImgIdx] = useState<number | null>(null);

  const handleMainImageUpload = async (file: File) => {
    try {
      setImageUploading(true);
      const compressed = await compressProductImage(file);
      setFormData(prev => ({ ...prev, image_url: compressed }));
    } catch {
      alert('Chyba při nahrávání obrázku');
    } finally {
      setImageUploading(false);
    }
  };

  const handleGalleryImagesUpload = async (files: FileList) => {
    setImageUploading(true);
    const newImages: string[] = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const compressed = await compressProductImage(files[i]);
        newImages.push(compressed);
      } catch {}
    }
    setFormData(prev => ({ ...prev, images: [...prev.images, ...newImages] }));
    setImageUploading(false);
  };

  const removeGalleryImage = (index: number) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleImgDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedImgIdx === null || draggedImgIdx === index) return;
    const newImages = [...formData.images];
    const dragged = newImages[draggedImgIdx];
    newImages.splice(draggedImgIdx, 1);
    newImages.splice(index, 0, dragged);
    setFormData(prev => ({ ...prev, images: newImages }));
    setDraggedImgIdx(index);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-green-500/30 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            {article ? 'Upravit technologii' : 'Přidat technologii'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4">
          <div>
            <label className="block text-slate-300 mb-2">Název <span className="text-red-400">*</span></label>
            <input type="text" value={formData.title_cz} onChange={(e) => setFormData({ ...formData, title_cz: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500" required />
          </div>
          <div>
            <label className="block text-slate-300 mb-2">Popis <span className="text-red-400">*</span></label>
            <textarea value={formData.description_cz} onChange={(e) => setFormData({ ...formData, description_cz: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500" rows={4} required />
          </div>

          <div>
            <label className="block text-slate-300 mb-2">Hlavní obrázek</label>
            <label className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-600 hover:border-green-500/50 rounded-lg p-4 cursor-pointer transition-colors ${imageUploading ? 'opacity-50 pointer-events-none' : ''}`}>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleMainImageUpload(f); }} />
              <Upload className="w-5 h-5 text-slate-400" />
              <span className="text-slate-400 text-sm">{imageUploading ? 'Nahrávám...' : 'Nahrát hlavní obrázek'}</span>
            </label>
            {formData.image_url && (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-800 mt-2">
                <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                  className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 rounded-full p-1 text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-slate-300 mb-2 flex items-center gap-2">
              <Images className="w-4 h-4 text-green-400" />Galerie obrázků
            </label>
            <label className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-600 hover:border-green-500/50 rounded-lg p-4 cursor-pointer transition-colors mb-3 ${imageUploading ? 'opacity-50 pointer-events-none' : ''}`}>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { if (e.target.files && e.target.files.length > 0) handleGalleryImagesUpload(e.target.files); }} />
              <Upload className="w-5 h-5 text-slate-400" />
              <span className="text-slate-400 text-sm">{imageUploading ? 'Nahrávám...' : 'Nahrát více obrázků do galerie'}</span>
            </label>
            {formData.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {formData.images.map((img, idx) => (
                  <div key={idx} draggable
                    onDragStart={() => setDraggedImgIdx(idx)}
                    onDragOver={(e) => handleImgDragOver(e, idx)}
                    onDragEnd={() => setDraggedImgIdx(null)}
                    className={`relative aspect-square rounded-lg overflow-hidden bg-slate-800 cursor-grab active:cursor-grabbing group ${draggedImgIdx === idx ? 'opacity-50 scale-95' : ''}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <GripVertical className="w-5 h-5 text-white" />
                    </div>
                    <button type="button" onClick={() => removeGalleryImage(idx)}
                      className="absolute top-1 right-1 bg-red-600/80 hover:bg-red-500 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">{idx + 1}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-slate-300 mb-2 flex items-center gap-2">
              <Play className="w-4 h-4 text-green-400" />Video URL
            </label>
            <input type="url" value={formData.video_url} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
              placeholder="https://www.youtube.com/watch?v=..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-2">Datum</label>
              <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500" />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">Kategorie</label>
              <input type="text" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500" placeholder="např. Energie, AI..." />
            </div>
          </div>

          <details className="group">
            <summary className="cursor-pointer text-slate-400 hover:text-slate-200 text-sm py-2 select-none">Překlady (volitelné)</summary>
            <div className="mt-3 space-y-3 border-t border-slate-700 pt-4">
              <div className="grid grid-cols-2 gap-3">
                {(['sk', 'en', 'de'] as const).map(lang => (
                  <div key={lang}>
                    <label className="block text-slate-400 text-sm mb-1">Název ({lang.toUpperCase()})</label>
                    <input type="text" value={(formData as any)[`title_${lang}`]} onChange={(e) => setFormData({ ...formData, [`title_${lang}`]: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-green-500" />
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                {(['sk', 'en', 'de'] as const).map(lang => (
                  <div key={lang}>
                    <label className="block text-slate-400 text-sm mb-1">Popis ({lang.toUpperCase()})</label>
                    <textarea value={(formData as any)[`description_${lang}`]} onChange={(e) => setFormData({ ...formData, [`description_${lang}`]: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-green-500" rows={2} />
                  </div>
                ))}
              </div>
            </div>
          </details>

          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">Zrušit</button>
            <button type="submit" className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-lg transition-colors flex items-center gap-2">
              <Save className="w-4 h-4" />Uložit
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
